const INSTAGRAM_CLASS = "instagram-media";
const INSTAGRAM_REGISTERED_CLASS = "instagram-media-registered";
const INSTAGRAM_RENDERED_CLASS = "instagram-media-rendered";
const INSTAGRAM_PAYLOAD_ID = "data-instgrm-payload-id";
const INSTAGRAM_PAYLOAD_PREFIX = "instagram-media-payload-";
const INSTAGRAM_FRAME_PREFIX = "instagram-embed-";
const INSTAGRAM_TIMEOUT = 10000;

const THREADS_CLASS = "text-post-media";
const THREADS_REGISTERED_CLASS = "text-post-media-registered";
const THREADS_PAYLOAD_ID = "data-text-app-payload-id";
const THREADS_PAYLOAD_PREFIX = "text-post-media-payload-";
const THREADS_FRAME_PREFIX = "text-post-embed-";
const THREADS_TIMEOUT = 10000;
const VALID_THREADS_ORIGINS = ["threads.com", "threads.net"];

const INSTAGRAM_IFRAME_STYLE = `
  background-color: white;
  border-radius: 3px;
  border: 1px solid #dbdbdb;
  box-shadow: none;
  display: block;
  margin: 0;
  min-width: 326px;
  padding: 0;
`;

function getThreadsIframeStyle(background) {
    return `
background: ${background};
min-width:270px;
padding:0;
width:99.375%;
width:-webkit-calc(100% - 2px);
width:calc(100% - 2px);
border:none;
border-radius:12px;
height:0;
`;
}

let instagramId = 0;
let threadsId = 0;
let isListening = false;

const activeFrames = {};

function ensureListener() {
    if (isListening) {
        return;
    }
    window.addEventListener("message", handleMessage);
    isListening = true;
}

function findFrameState(event) {
    function hasMessageSource(state) {
        return state.frame.contentWindow === event.source;
    }

    return Object.values(activeFrames).find(hasMessageSource) || null;
}

function isSameOrSubdomain(hostname, domain) {
    return hostname === domain || hostname.endsWith(`.${domain}`);
}

function isInstagramOrigin(origin) {
    try {
        const { hostname } = new URL(origin);
        return isSameOrSubdomain(hostname, "instagram.com") || hostname === "instagr.am";
    } catch (error) {
        return false;
    }
}

function isThreadsOrigin(origin, state) {
    try {
        const { hostname, protocol } = new URL(origin);
        if (protocol !== "https:") {
            return false;
        }

        function hostMatchesAllowedOrigin(domain) {
            return isSameOrSubdomain(hostname, domain);
        }

        return state.allowedOrigins.some(hostMatchesAllowedOrigin);
    } catch (error) {
        return false;
    }
}

function complete(state) {
    if (state.completed) {
        return;
    }
    state.completed = true;
    clearTimeout(state.timeoutID);
    state.onComplete(state.frame);
}

function getPathWithTrailingSlash(url) {
    return url.replace(/^(.*?)\/?(\?.*|#.*|$)/, "$1/");
}

function getInstagramPermalink(target) {
    if (target.hasAttribute("data-instgrm-permalink")) {
        return target.getAttribute("data-instgrm-permalink");
    }

    function getNormalizedLinkPermalink(link) {
        return normalizeInstagramPermalink(link.href);
    }

    function hasPermalink(permalink) {
        return permalink;
    }

    return (
        Array.from(target.getElementsByTagName("a"))
            .reverse()
            .map(getNormalizedLinkPermalink)
            .find(hasPermalink) || null
    );
}

function normalizeInstagramPermalink(value) {
    try {
        const url = new URL(value, window.location.href);
        const isValidHost =
            isSameOrSubdomain(url.hostname, "instagram.com") || url.hostname === "instagr.am";
        const match = url.pathname.match(/^\/(p|reel|tv)\/[^/]+/);

        if (!isValidHost || !match) {
            return null;
        }

        return `https://www.instagram.com${match[0]}/`;
    } catch (error) {
        return null;
    }
}

function getWidthPixels(target) {
    const { clientWidth } = target;
    const { devicePixelRatio } = window;
    if (!clientWidth || !devicePixelRatio) {
        return 0;
    }
    return parseInt(clientWidth * devicePixelRatio, 10);
}

function buildInstagramSrc(target, permalink, frameIndex) {
    let src = `${getPathWithTrailingSlash(permalink)}embed/`;

    if (target.hasAttribute("data-instgrm-captioned")) {
        src += "captioned/";
    }

    src += "?cr=1";

    if (target.hasAttribute("data-instgrm-version")) {
        const version = parseInt(target.getAttribute("data-instgrm-version"), 10);
        if (!isNaN(version)) {
            src += `&v=${version}`;
        }
    }

    const widthPixels = getWidthPixels(target);
    if (widthPixels) {
        src += `&wp=${widthPixels}`;
    }

    src += `&rd=${encodeURIComponent(window.location.origin)}`;

    const currentPath = window.location.pathname;
    if (currentPath) {
        const referrerPath = `${currentPath}${window.location.search || ""}`;
        src += `&rp=${encodeURIComponent(referrerPath.substring(0, 200))}`;
    }

    const domainVariant = permalink.match(/__d=(www|dis)/);
    if (domainVariant) {
        src += `&${domainVariant[0]}`;
    }

    return `${src.replace(/^http:\/\//, "https://")}#${encodeURIComponent(
        JSON.stringify({ ci: frameIndex }),
    )}`;
}

function createState(frame, target, type, timeout, onComplete, extraState) {
    const state = {
        ...extraState,
        frame,
        target,
        type,
        completed: false,
        onComplete,
    };

    function onEmbedTimeout() {
        complete(state);
    }

    state.timeoutID = window.setTimeout(onEmbedTimeout, timeout);
    activeFrames[frame.id] = state;
    ensureListener();
    return state;
}

export function renderInstagramEmbed(target, onComplete) {
    const permalink = normalizeInstagramPermalink(getInstagramPermalink(target));
    if (!permalink) {
        return null;
    }

    const frameIndex = instagramId++;
    const frameID = `${INSTAGRAM_FRAME_PREFIX}${frameIndex}`;

    if (!target.id) {
        target.id = `${INSTAGRAM_PAYLOAD_PREFIX}${frameIndex}`;
    }

    const frame = document.createElement("iframe");
    frame.className = target.className;
    frame.id = frameID;
    frame.src = buildInstagramSrc(target, permalink, frameIndex);
    frame.setAttribute("allowTransparency", "true");
    frame.setAttribute("allowfullscreen", "true");

    const originalPosition = target.style.position;
    if (originalPosition) {
        frame.setAttribute("data-instgrm-preserve-position", originalPosition);
    }

    frame.setAttribute("frameBorder", "0");
    frame.setAttribute("height", "0");
    frame.setAttribute(INSTAGRAM_PAYLOAD_ID, target.id);
    frame.setAttribute("scrolling", "no");
    frame.setAttribute("style", `${target.style.cssText};${INSTAGRAM_IFRAME_STYLE}`);
    frame.style.position = "absolute";

    target.parentNode.insertBefore(frame, target);
    target.classList.add(INSTAGRAM_REGISTERED_CLASS);
    target.classList.remove(INSTAGRAM_CLASS);

    createState(frame, target, "instagram", INSTAGRAM_TIMEOUT, onComplete, {
        heights: {},
    });

    return frame;
}

function applyInstagramStyles(frame, styles) {
    if (!Array.isArray(styles)) {
        return;
    }

    function applyStylePair(stylePair) {
        if (Array.isArray(stylePair) && stylePair.length >= 2) {
            frame.style[stylePair[0]] = stylePair[1];
        }
    }

    styles.forEach(applyStylePair);
}

function handleInstagramMessage(event, state) {
    if (!isInstagramOrigin(event.origin)) {
        return;
    }

    let message;
    try {
        message = JSON.parse(event.data);
    } catch (error) {
        return;
    }

    if (!message || typeof message.type !== "string" || typeof message.details !== "object") {
        return;
    }

    const { frame } = state;
    const payload = document.getElementById(frame.getAttribute(INSTAGRAM_PAYLOAD_ID));
    let height = null;

    switch (message.type) {
        case "MOUNTED":
            height = payload ? payload.clientHeight : state.target.clientHeight;
            frame.style.position = frame.hasAttribute("data-instgrm-preserve-position")
                ? frame.getAttribute("data-instgrm-preserve-position")
                : "";
            applyInstagramStyles(frame, message.details.styles);
            frame.classList.add(INSTAGRAM_RENDERED_CLASS);
            if (payload && payload.parentNode) {
                payload.parentNode.removeChild(payload);
            }
            complete(state);
            break;
        case "MEASURE":
            if (state.heights[frame.id] !== message.details.height) {
                height = message.details.height;
            }
            break;
        case "UNMOUNTING":
            delete state.heights[frame.id];
            break;
        case "LOADING":
            break;
        default:
            return;
    }

    if (height !== null && height !== undefined) {
        frame.height = state.heights[frame.id] = height;
    }
}

function getThreadsPermalink(target) {
    return target.getAttribute("data-text-post-permalink");
}

function normalizeThreadsPermalink(value) {
    try {
        const url = new URL(value, window.location.href);
        const isValidHost =
            isSameOrSubdomain(url.hostname, "threads.com") ||
            isSameOrSubdomain(url.hostname, "threads.net");
        const match = url.pathname.match(/^\/@[^/]+\/post\/[^/]+/);

        if (!isValidHost || !match) {
            return null;
        }

        return `https://${url.hostname}${match[0]}/`;
    } catch (error) {
        return null;
    }
}

function buildThreadsSrc(target, permalink) {
    let src = getPathWithTrailingSlash(permalink);
    src += "embed/";

    const theme = target.getAttribute("data-theme");
    if (theme === "dark" || theme === "auto") {
        src += `${src.includes("?") ? "&" : "?"}theme=${encodeURIComponent(theme)}`;
    }

    return src;
}

function getThreadsAllowedOrigins(permalink) {
    try {
        const { hostname } = new URL(permalink, window.location.href);

        return Array.from(new Set([...VALID_THREADS_ORIGINS, hostname]));
    } catch (error) {}

    return VALID_THREADS_ORIGINS;
}

export function renderThreadsEmbed(target, onComplete) {
    const permalink = normalizeThreadsPermalink(getThreadsPermalink(target));
    if (!permalink) {
        return null;
    }

    const frameIndex = threadsId++;
    const frameID = `${THREADS_FRAME_PREFIX}${frameIndex}`;
    const theme = target.getAttribute("data-theme");
    const background = theme === "dark" || theme === "auto" ? "transparent" : "#FFF";

    if (!target.id) {
        target.id = `${THREADS_PAYLOAD_PREFIX}${frameIndex}`;
    }

    const frame = document.createElement("iframe");
    frame.className = target.className;
    frame.id = frameID;
    frame.src = buildThreadsSrc(target, permalink);
    frame.setAttribute("allowTransparency", "true");
    frame.setAttribute("allowfullscreen", "true");
    frame.setAttribute("frameBorder", "0");
    frame.setAttribute("height", "0");
    frame.setAttribute(THREADS_PAYLOAD_ID, target.id);
    frame.setAttribute("scrolling", "no");
    frame.setAttribute("style", `${target.style.cssText};${getThreadsIframeStyle(background)}`);

    target.parentNode.insertBefore(frame, target);
    target.classList.add(THREADS_REGISTERED_CLASS);
    target.classList.remove(THREADS_CLASS);

    createState(frame, target, "threads", THREADS_TIMEOUT, onComplete, {
        allowedOrigins: getThreadsAllowedOrigins(permalink),
    });

    return frame;
}

function handleThreadsMessage(event, state) {
    if (!isThreadsOrigin(event.origin, state)) {
        return;
    }

    const height = Number(event.data);
    if (isNaN(height)) {
        return;
    }

    const { frame } = state;
    const payload = document.getElementById(frame.getAttribute(THREADS_PAYLOAD_ID));

    if (payload && payload.parentNode) {
        payload.parentNode.removeChild(payload);
    }

    frame.style.height = `${height}px`;
    frame.height = height;
    complete(state);
}

export function handleMessage(event) {
    const state = findFrameState(event);
    if (!state) {
        return;
    }

    if (state.type === "instagram") {
        handleInstagramMessage(event, state);
    } else if (state.type === "threads") {
        handleThreadsMessage(event, state);
    }
}
